import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLatestAssignedAtForCurrentStudent } from "@/services/challengeAssignmentsService";

const POLL_INTERVAL_MS = 60_000;
const TOAST_ID = "new-assignment-toast";

/**
 * Polls the database for newly assigned challenges and shows a persistent
 * dismissable toast when one appears. Runs only for students.
 *
 * The toast offers a "refresh" action that hard-reloads the page so the
 * student's cached challenge list is rebuilt fresh.
 */
export function useNewAssignmentNotifier(): void {
  const { user, profile } = useAuth();
  const { t } = useLanguage();

  const baselineRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  const isToastOpenRef = useRef(false);

  const isStudent = profile?.role === "student";
  const userId = user?.id;

  useEffect(() => {
    if (!isStudent || !userId) {
      baselineRef.current = null;
      isInitializedRef.current = false;
      isToastOpenRef.current = false;
      return;
    }

    let isCancelled = false;

    const showToast = () => {
      if (isToastOpenRef.current) return;
      isToastOpenRef.current = true;

      toast.info(
        t(
          "Vypadá to, že máš nový úkol. Aktualizuj stránku, aby ses k němu dostal.",
          "Vyzerá to tak, že máš novú úlohu. Aktualizuj stránku, aby si sa k nej dostal."
        ),
        {
          id: TOAST_ID,
          duration: Infinity,
          action: {
            label: t("Aktualizovat", "Aktualizovať"),
            onClick: () => {
              window.location.reload();
            },
          },
          onDismiss: () => {
            isToastOpenRef.current = false;
          },
          onAutoClose: () => {
            isToastOpenRef.current = false;
          },
        }
      );
    };

    const checkForNewAssignment = async () => {
      if (document.visibilityState !== "visible") return;

      const latest = await getLatestAssignedAtForCurrentStudent();
      if (isCancelled) return;

      if (!isInitializedRef.current) {
        baselineRef.current = latest;
        isInitializedRef.current = true;
        return;
      }

      if (!latest) return;

      const isNewer =
        baselineRef.current === null ||
        new Date(latest).getTime() > new Date(baselineRef.current).getTime();

      if (isNewer) {
        baselineRef.current = latest;
        showToast();
      }
    };

    void checkForNewAssignment();

    const intervalId = window.setInterval(() => {
      void checkForNewAssignment();
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForNewAssignment();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      toast.dismiss(TOAST_ID);
      isToastOpenRef.current = false;
    };
  }, [isStudent, userId, t]);
}
